import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { OfflineIndicator } from '../shared/OfflineIndicator';
import { 
  LayoutDashboard, 
  Store, 
  MapPin, 
  BarChart3,
  Package,
  Truck,
  Warehouse,
  ShoppingCart,
  ClipboardList,
  LogOut, 
  Menu,
  X,
  Settings,
  Users,
  Building2,
  Navigation,
  FileText,
  UserPlus,
  UserCircle,
  Trophy,
  LucideIcon
} from 'lucide-react';
import { UserAvatar } from '../shared/UserAvatar';
import { ActiveWorkFabCluster } from './ActiveWorkFabCluster';
import { useActiveVisit } from '../../contexts/ActiveVisitContext';

interface LayoutProps {
  children: React.ReactNode;
}

// Navigation item with optional module requirements
interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  moduleIds?: string[];
  section: 'work' | 'documents' | 'reports' | 'more';
}

const SECTION_LABELS: Record<NavItem['section'], string | null> = {
  work: null,
  documents: 'Documents',
  reports: null,
  more: 'More',
};

const allNavItems: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, section: 'work' },
  { name: 'Leaderboard', href: '/leaderboard', icon: Trophy, section: 'work' },
  { name: 'Points of Sale', href: '/pos', icon: Store, moduleIds: ['pos_management'], section: 'work' },
  { name: 'Visits', href: '/visits', icon: MapPin, moduleIds: ['visit_firsttime', 'visit_revisit', 'visit_product_intro'], section: 'work' },
  { name: 'Tasks', href: '/todos', icon: ClipboardList, moduleIds: ['todos'], section: 'work' },
  { name: 'Routes', href: '/routes', icon: Navigation, moduleIds: ['route_batches'], section: 'work' },
  { name: 'Delivery Batches', href: '/delivery-batches', icon: Truck, moduleIds: ['delivery_batches'], section: 'work' },
  { name: 'Orders', href: '/orders', icon: ShoppingCart, moduleIds: ['orders'], section: 'documents' },
  { name: 'Delivery notes', href: '/lieferscheine', icon: FileText, moduleIds: ['delivery_batches'], section: 'documents' },
  { name: 'Reports', href: '/reports', icon: BarChart3, moduleIds: ['reports_basic'], section: 'reports' },
  { name: 'Manufacturers', href: '/manufacturers', icon: Building2, moduleIds: ['products'], section: 'more' },
  { name: 'Products', href: '/products', icon: Package, moduleIds: ['products'], section: 'more' },
  { name: 'Distributors', href: '/distributors', icon: Warehouse, moduleIds: ['distributors'], section: 'more' },
  { name: 'Onboarding', href: '/onboardings', icon: UserPlus, section: 'more' },
  { name: 'Profile', href: '/profile', icon: UserCircle, section: 'more' },
  { name: 'Settings', href: '/settings', icon: Settings, section: 'more' },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin, isTeamAdmin, hasModule } = useAuth();
  const location = useLocation();
  const { visitsHref } = useActiveVisit();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Build navigation based on user role and enabled modules
  const navigation = React.useMemo(() => {
    // Filter navigation items by module access
    const items: NavItem[] = allNavItems.filter(item => {
      // Items without moduleIds are always visible (e.g., Dashboard)
      if (!item.moduleIds) return true;
      // Show item if user has at least one of the required modules
      return item.moduleIds.some(moduleId => hasModule(moduleId));
    });

    // Add admin section for super_admin
    if (isSuperAdmin) {
      items.push({
        name: 'Manage Teams',
        href: '/admin/teams',
        icon: Building2,
        section: 'more',
      });
    }

    if (isTeamAdmin) {
      items.push(
        { name: 'Team Settings', href: '/team/settings', icon: Settings, section: 'more' },
        { name: 'Team Users', href: '/team/users', icon: Users, section: 'more' }
      );
    }

    return items;
  }, [hasModule, isSuperAdmin, isTeamAdmin]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const navSections: NavItem['section'][] = ['work', 'documents', 'reports', 'more'];

  const renderNavLinks = (onNavigate?: () => void) =>
    navSections.map((section) => {
      const items = navigation.filter((item) => item.section === section);
      if (items.length === 0) return null;
      const label = SECTION_LABELS[section];
      return (
        <div key={section} className="space-y-1">
          {label && (
            <p className="px-3 pt-3 pb-1 text-[11px] font-black uppercase tracking-widest text-gray-400">
              {label}
            </p>
          )}
          {items.map((item) => (
            <Link
              key={item.name}
              to={item.href === '/visits' ? visitsHref : item.href}
              onClick={onNavigate}
              className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                isActive(item.href)
                  ? 'bg-brand-50 text-brand-700 shadow-sm ring-1 ring-brand-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon
                className={`mr-3 h-5 w-5 flex-shrink-0 ${
                  isActive(item.href) ? 'text-brand-600' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {item.name}
            </Link>
          ))}
        </div>
      );
    });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <div className="bg-brand-600 rounded-lg p-1.5">
            <span className="text-white font-bold text-lg leading-none">BC</span>
          </div>
          <span className="font-semibold text-gray-900">BC-CRM</span>
        </div>
        <div className="flex items-center gap-2">
          <OfflineIndicator />
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu (Overlay) */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-gray-800 bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-64 bg-white shadow-xl p-4 flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-4 flex-shrink-0">
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <div className="space-y-1 flex-1 overflow-y-auto min-h-0">
              {renderNavLinks(() => setIsMobileMenuOpen(false))}
            </div>
            <div className="border-t border-gray-200 pt-4 flex-shrink-0">
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center px-2 mb-4 rounded-md hover:bg-gray-50"
              >
                {user && (
                  <UserAvatar
                    userId={user.id}
                    fullName={user.fullName}
                    photoUrl={user.photoUrl}
                    size="sm"
                  />
                )}
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{user?.fullName}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    {user?.role === 'super_admin' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 flex-shrink-0">
                        Super Admin
                      </span>
                    )}
                    {user?.role === 'team_admin' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 flex-shrink-0">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              </Link>
              <button
                onClick={logout}
                className="w-full flex items-center px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-gray-200">
        <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200 bg-white">
          <div className="bg-brand-600 rounded-lg p-1.5 mr-3">
            <span className="text-white font-bold text-lg leading-none">BC</span>
          </div>
          <span className="text-xl font-bold text-gray-900 flex-1">BC-CRM</span>
          <OfflineIndicator />
        </div>
        <div className="flex-1 flex flex-col overflow-y-auto pt-5 pb-4">
          <nav className="flex-1 px-4 space-y-1">
            {renderNavLinks()}
          </nav>
          
          <div className="flex-shrink-0 border-t border-gray-200">
            <div className="p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <Link to="/profile" className="flex items-center flex-1 min-w-0">
                    {user && (
                      <UserAvatar
                        userId={user.id}
                        fullName={user.fullName}
                        photoUrl={user.photoUrl}
                        size="md"
                      />
                    )}
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                        {user?.fullName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {user?.role === 'super_admin' && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700">
                            Super Admin
                          </span>
                        )}
                        {user?.role === 'team_admin' && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700">
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="ml-2 flex flex-col items-end gap-1">
                    <button
                      onClick={logout}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <LogOut className="h-3 w-3 mr-1" />
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 px-4 py-2 text-center">
              <p className="text-xs text-gray-400">Developed by Era Digitalis GmbH</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 md:pl-64 flex flex-col min-h-screen overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-24">
          {children}
        </div>
      </main>
      <ActiveWorkFabCluster />
    </div>
  );
};

